import {
  Component,
  h,
  State,
  Element,
  Prop,
  Listen,
  Event,
  type EventEmitter,
  Host,
} from '@stencil/core';
import { isMobile } from '../../utils/media';
import { searchOverflowToggler } from '../../utils/helpers/overflowToggler';
import { productNaSelectorWidgetEventEmitter } from './utils';
import type { labelProviderInterface } from '../LabelProviderInterface';

@Component({
  tag: 'uiaas-product-na-country-selector-banner',
  shadow: true,
  styleUrl: 'uiaas-product-na-country-selector-banner.scss',
})
export class ProductNaCountrySelectorBanner {
  @State() isVisibleBanner: boolean = true;
  @State() isMobile: boolean = isMobile();

  @Prop() readonly labelProvider: labelProviderInterface;
  @Prop() readonly country: string;
  @Prop() readonly language: string;
  /*
   * total number of all available documents
   */
  @Prop() readonly totalHits: number | undefined;
  /*
   * List of locations where the searched product is available
   */
  @Prop() readonly productNaLocales?: string;
  /*
   * Is header displayed 'Products' section label should be displayed
   * Header is displayed only on Results Page, not dispalyed on No Results Page
   */
  @Prop() readonly isHeaderVisible: boolean = false;
  @Prop() readonly tabName: string;
  /*
   * Location of the GA analytics event
   */
  @Prop() readonly location: string;
  @Prop() readonly rtl: boolean;

  @Element() element;

  private countrySelectorWidgetElement = document.createElement(
    'uiaas-product-na-country-selector-widget',
  );

  private accessibilityMode = false;

  @Event({
    eventName: 'search:productNaSelector',
  })
    SearchProductNaSelectorEmitterSRP: EventEmitter;

  @Listen('uiaas:accessibility', { target: 'window' })
  handleAccessibility(ev) {
      this.accessibilityMode = ev.detail;
  }

  private loadWidgetToDocumentBody() {
      const widgetTitle = this.labelProvider(
        'product-na-country-selector.widget.header',
        'Select a location',
      );

      this.countrySelectorWidgetElement.setAttribute(
        'total-hits',
        this.totalHits?.toString(),
      );

      this.countrySelectorWidgetElement.setAttribute(
        'accessibilityMode',
        this.accessibilityMode.toString(),
      );

      this.countrySelectorWidgetElement.setAttribute('content-text', widgetTitle);
      this.countrySelectorWidgetElement.setAttribute('country', this.country);
      this.countrySelectorWidgetElement.setAttribute('language', this.language);
      this.countrySelectorWidgetElement.setAttribute('location', this.location);
      this.countrySelectorWidgetElement.setAttribute('tab-name', this.tabName);
      this.countrySelectorWidgetElement.setAttribute(
        'locales',
        this.productNaLocales,
      );

      if (
        !document.body.querySelector('uiaas-product-na-country-selector-widget')
      )
        document.body.append(this.countrySelectorWidgetElement);
      searchOverflowToggler(true);
  }

  render() {
      return (
        this.productNaLocales && (
          <Host>
            {this.isHeaderVisible && (
              <section class="item-header">
                <p class="item-header__title">
                  {this.labelProvider('tab.se/products', 'Products')}
                </p>
              </section>
            )}
            <div
              class={{
                'smart-country-selector-banner': true,
                'smart-country-selector-banner--disabled': !this.isVisibleBanner,
                'smart-country-selector-banner--srp-variant': true,
              }}
            >
              <div>
                <div class="smart-country-selector-banner-content">
                  <span
                    class="smart-country-selector-banner-content__icon"
                    dir={this.rtl ? 'rtl' : 'auto'}
                  >
                    <se-icon size="large">web_sdk</se-icon>
                  </span>
                  <div>
                    <div class="smart-country-selector-banner-content-header">
                      <p>
                        {this.labelProvider(
                          'product-na-country-selector.banner.header',
                          'Did not find the product you are looking for?',
                        )}
                      </p>
                    </div>
                    <span>
                      {this.labelProvider(
                        'product-na-country-selector.banner.header.content',
                        'Explore where this product is available by selecting a country or region, and get in touch with local support for additional information.',
                      )}
                    </span>
                  </div>
                </div>
                <section class="smart-country-selector-banner__button-section">
                  <qds-button
                    importance="emphasized"
                    size="large"
                    class="smart-country-selector-banner__button"
                    onClick={() => {
                        this.loadWidgetToDocumentBody();
                        productNaSelectorWidgetEventEmitter(
                          this.SearchProductNaSelectorEmitterSRP,
                          this.location,
                          this.tabName,
                          'left',
                        );
                    }}
                    text={this.labelProvider(
                      'smart.country.selector.banner.cta',
                      'Select location',
                    )}
                  ></qds-button>
                </section>
              </div>
            </div>
          </Host>
        )
      );
  }
}
